import { Decimal } from 'decimal.js';
/**
 * local-ledger money math — the ONE implementation of the formulas in the
 * local-ledger spec («Розрахунки»). Pure; the API materializes the result into
 * `local_vehicles.financials` on every write, the import replays it for parity.
 *
 *   purchaseUsd    = purchasePrice × purchaseFxRateToUsd
 *   expensesUsd    = Σ expense.amount × expense.fxRateToUsd
 *   costUsd        = purchaseUsd + expensesUsd
 *   saleUsd        = salePrice × saleFxRateToUsd                 (stage = sold)
 *   profitUsd      = saleUsd − costUsd                           (stage = sold)
 *   margin         = profitUsd / costUsd                         (null at costUsd = 0)
 *   daysInStock    = saleDate − purchaseDate  (today − purchaseDate while open)
 *   fxDeviationUsd = Σ amount × (fxRateToUsd − officialFxRateToUsd)  (official known)
 *   balanceUsd     = Σ(deposit + opening) − Σ withdrawal − Σ purchaseUsd − Σ expensesUsd + Σ saleUsd
 *
 * Everything runs on `decimal.js`, never `number`. Each line converted to USD
 * is rounded to the cent (half away from zero) BEFORE it is summed, so a total
 * always equals the sum of the rows a report prints. Money leaves as
 * `toFixed(2)`, `margin` as `toFixed(4)`.
 */
/** Private constructor: never mutate the global `Decimal` config another package may rely on. */
const D = Decimal.clone({ precision: 40, rounding: Decimal.ROUND_HALF_UP });
const MS_PER_DAY = 86_400_000;
function money(value) {
    // `toFixed` keeps the sign of a rounded-to-zero negative ("-0.00"); ledgers print "0.00".
    const fixed = value.toFixed(2);
    return fixed === '-0.00' ? '0.00' : fixed;
}
/** `amount × rate`, rounded to the cent. */
export function mulMoney(amount, rate) {
    return money(new D(amount).times(rate).toDecimalPlaces(2));
}
/** Σ amounts, as money. An empty list is `"0.00"`. */
export function sumMoney(amounts) {
    return money(amounts.reduce((acc, a) => acc.plus(a), new D(0)));
}
/** `numerator / denominator` as a four-decimal ratio (`margin`, win rate); null at a zero denominator. */
export function ratio(numerator, denominator) {
    const d = new D(denominator);
    return d.isZero() ? null : new D(numerator).dividedBy(d).toFixed(4);
}
/** Mean of money amounts, to the cent; null for an empty list. */
export function averageMoney(amounts) {
    if (amounts.length === 0)
        return null;
    return money(amounts
        .reduce((acc, a) => acc.plus(a), new D(0))
        .dividedBy(amounts.length)
        .toDecimalPlaces(2));
}
/** Mean of four-decimal ratios, to four decimals; null for an empty list. */
export function averageRatio(values) {
    if (values.length === 0)
        return null;
    return values
        .reduce((acc, v) => acc.plus(v), new D(0))
        .dividedBy(values.length)
        .toFixed(4);
}
/**
 * `numerator / denominator` as an FX rate (USD per one unit): six decimals,
 * half-up, trailing zeros dropped (`"1.1035"`). Null at a zero denominator or
 * when the quotient rounds to zero — `FxRateStringSchema` refuses a zero rate.
 * The NBU sync derives `rateToUsd(CHF) = UAH-per-CHF / UAH-per-USD` with it.
 */
export function crossRate(numerator, denominator) {
    const d = new D(denominator);
    if (d.isZero())
        return null;
    const q = new D(numerator).dividedBy(d).toDecimalPlaces(6);
    return q.isZero() || q.isNegative() ? null : q.toFixed();
}
/** Whole days from `from` to `to` (`YYYY-MM-DD`, calendar days, no time zone drift). */
export function daysBetween(from, to) {
    return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / MS_PER_DAY);
}
function bookedRate(line) {
    return line.currency === 'USD' ? '1' : line.fxRateToUsd;
}
/** USD value of one line, or null when its rate is unknown. */
function lineUsd(line) {
    const rate = bookedRate(line);
    return rate === null ? null : mulMoney(line.amount, rate);
}
/**
 * The vehicle's `financials`. Any value that depends on a missing rate is null
 * and `status` is `incomplete` — a missing rate never silently counts as 0
 * (the spreadsheet bug that showed a fake 5 000 $ profit).
 */
export function computeVehicleFinancials(input) {
    const sold = input.stage === 'sold';
    const purchaseUsd = lineUsd(input.purchase);
    const expenseLines = input.expenses.map(lineUsd);
    const expensesUsd = expenseLines.every((v) => v !== null)
        ? sumMoney(expenseLines)
        : null;
    const costUsd = purchaseUsd !== null && expensesUsd !== null ? sumMoney([purchaseUsd, expensesUsd]) : null;
    const saleUsd = sold && input.sale ? lineUsd(input.sale) : null;
    const profitUsd = saleUsd !== null && costUsd !== null ? money(new D(saleUsd).minus(costUsd)) : null;
    const margin = profitUsd !== null && costUsd !== null && !new D(costUsd).isZero()
        ? new D(profitUsd).dividedBy(costUsd).toFixed(4)
        : null;
    const end = sold && input.saleDate ? input.saleDate : input.today;
    const daysInStock = daysBetween(input.purchaseDate, end);
    // USD lines deviate by definition by 0 and are left out, so a vehicle with no
    // known official rate reports null, not a misleading "0.00".
    const deviationLines = [
        input.purchase,
        ...input.expenses,
        ...(sold && input.sale ? [input.sale] : []),
    ]
        .filter((line) => line.currency !== 'USD')
        .flatMap((line) => {
        const official = line.officialFxRateToUsd ?? null;
        if (line.fxRateToUsd === null || official === null)
            return [];
        return [
            money(new D(line.amount).times(new D(line.fxRateToUsd).minus(official)).toDecimalPlaces(2)),
        ];
    });
    const fxDeviationUsd = deviationLines.length > 0 ? sumMoney(deviationLines) : null;
    const incomplete = purchaseUsd === null || expensesUsd === null || (sold && saleUsd === null);
    return {
        status: incomplete ? 'incomplete' : 'ok',
        purchaseUsd,
        expensesUsd,
        costUsd,
        saleUsd,
        profitUsd,
        margin,
        daysInStock,
        fxDeviationUsd,
    };
}
/**
 * The trading budget: what was put in, minus what was taken out, minus what
 * the cars cost, plus what they sold for. Purchases, expenses and sales move
 * the balance without a `local_budget_movements` row (SC-8). A vehicle whose
 * USD figure is unknown contributes only its known parts and is counted in
 * `incompleteVehicles` so the UI can say the balance is partial.
 */
export function computeBudgetBalance(movements, vehicles) {
    const inflowsUsd = sumMoney(movements.filter((m) => m.type !== 'withdrawal').map((m) => m.amountUsd));
    const withdrawalsUsd = sumMoney(movements.filter((m) => m.type === 'withdrawal').map((m) => m.amountUsd));
    const known = (values) => values.filter((v) => v !== null);
    const purchasesUsd = sumMoney(known(vehicles.map((v) => v.purchaseUsd)));
    const expensesUsd = sumMoney(known(vehicles.map((v) => v.expensesUsd)));
    const salesUsd = sumMoney(known(vehicles.filter((v) => v.stage === 'sold').map((v) => v.saleUsd)));
    const incompleteVehicles = vehicles.filter((v) => v.purchaseUsd === null ||
        v.expensesUsd === null ||
        (v.stage === 'sold' && v.saleUsd === null)).length;
    const balanceUsd = money(new D(inflowsUsd).minus(withdrawalsUsd).minus(purchasesUsd).minus(expensesUsd).plus(salesUsd));
    return {
        balanceUsd,
        inflowsUsd,
        withdrawalsUsd,
        purchasesUsd,
        expensesUsd,
        salesUsd,
        incompleteVehicles,
    };
}
//# sourceMappingURL=local-ledger-math.js.map